/**
 * Firmendaten und Nummernkreise.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  BUNDESLAENDER,
  formatIban,
  INTRASTAT_TRANSACTION_TYPES,
  INTRASTAT_TRANSPORT_MODES,
  MODULES,
} from '@erp/shared';

import { ApiError, api } from '../api/client';
import type { Company, NumberRange } from '../api/types';
import { useAuth } from '../auth';
import { PageHeader } from '../components/Layout';
import { useToast } from '../components/toast';
import { Alert, Badge, Field, LoadingBlock, Select, TextArea, TextInput } from '../components/ui';
import { CategoriesPage } from './CategoriesPage';
import { TemplateDesignPage } from './TemplateDesignPage';
import { WarehousesPage } from './WarehousesPage';

type Tab = 'company' | 'numbers' | 'categories' | 'warehouses' | 'design' | 'modules';

export function SettingsPage() {
  const [tab, setTab] = useState<Tab>('company');

  const tabs: { key: Tab; label: string }[] = [
    { key: 'company', label: 'Firmendaten' },
    { key: 'numbers', label: 'Nummernkreise' },
    { key: 'categories', label: 'Warengruppen' },
    { key: 'warehouses', label: 'Lagerorte' },
    { key: 'design', label: 'Belegdesign' },
    { key: 'modules', label: 'Module' },
  ];

  return (
    <>
      <PageHeader title="Einstellungen" />

      <div className="row" style={{ marginBottom: 16, gap: 6 }}>
        {tabs.map((entry) => (
          <button
            key={entry.key}
            type="button"
            className={`btn btn-sm${tab === entry.key ? ' btn-primary' : ''}`}
            onClick={() => setTab(entry.key)}
          >
            {entry.label}
          </button>
        ))}
      </div>

      {tab === 'company' ? <CompanySettings /> : null}
      {tab === 'numbers' ? <NumberRangeSettings /> : null}
      {tab === 'categories' ? <CategoriesPage /> : null}
      {tab === 'warehouses' ? <WarehousesPage /> : null}
      {tab === 'design' ? <TemplateDesignPage /> : null}
      {tab === 'modules' ? <ModuleSettings /> : null}
    </>
  );
}

/**
 * Anzeige der Fachmodule dieser Installation.
 *
 * Bewusst nur eine Anzeige: Module werden in der Server-Konfiguration
 * geschaltet (CANALIS_MODULES), nicht in der Oberfläche. Ein Modul
 * an- oder abzuschalten ist eine Betriebsentscheidung je Installation
 * und soll nicht versehentlich per Mausklick passieren.
 */
function ModuleSettings() {
  const { moduleEnabled } = useAuth();

  return (
    <div className="card">
      <div className="card-header">
        <h2>Fachmodule</h2>
      </div>

      <Alert kind="info">
        Module werden in der Server-Konfiguration dieser Installation festgelegt
        (Umgebungsvariable <code>CANALIS_MODULES</code>). Nach einer Änderung muss der
        Server neu gestartet werden.
      </Alert>

      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th>Modul</th>
              <th>Beschreibung</th>
              <th className="shrink">Status</th>
            </tr>
          </thead>
          <tbody>
            {MODULES.map((entry) => (
              <tr key={entry.key}>
                <td style={{ fontWeight: 500 }}>{entry.label}</td>
                <td className="muted">{entry.description}</td>
                <td>
                  {moduleEnabled(entry.key) ? (
                    <Badge kind="success">Aktiv</Badge>
                  ) : (
                    <Badge>Nicht aktiv</Badge>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CompanySettings() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { can } = useAuth();
  const readOnly = !can('settings:write');

  const [form, setForm] = useState<Company | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: loaded, isLoading } = useQuery({
    queryKey: ['company'],
    queryFn: () => api.get<{ company: Company }>('/api/settings/company'),
  });
  // Formular aus den Daten fuellen, nicht in der Ladefunktion: Aus dem
  // Zwischenspeicher laeuft die Ladefunktion nicht.
  useEffect(() => {
    if (loaded) setForm(loaded.company);
  }, [loaded]);

  const save = useMutation({
    mutationFn: (values: Company) => api.put<{ company: Company }>('/api/settings/company', values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company'] });
      toast.success('Die Firmendaten wurden gespeichert.');
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        setErrors(error.fieldErrors);
        toast.error(error.message);
      } else {
        toast.error('Beim Speichern ist ein Fehler aufgetreten.');
      }
    },
  });

  if (isLoading || !form) return <LoadingBlock />;

  function update<K extends keyof Company>(key: K, value: Company[K]) {
    setForm((current) => (current ? { ...current, [key]: value } : current));
    setErrors((current) => {
      if (!current[key as string]) return current;
      const next = { ...current };
      delete next[key as string];
      return next;
    });
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        setErrors({});
        save.mutate(form!);
      }}
    >
      <div className="card">
        <div className="card-body">
          <fieldset>
            <legend>Firma</legend>
            <div className="form-grid">
              <Field label="Firmenname" span={8} required error={errors.name}>
                <TextInput
                  value={form.name}
                  onChange={(value) => update('name', value)}
                  invalid={Boolean(errors.name)}
                  disabled={readOnly}
                />
              </Field>
              <Field label="Rechtsform" span={4}>
                <TextInput
                  value={form.legalForm}
                  onChange={(value) => update('legalForm', value)}
                  disabled={readOnly}
                />
              </Field>
              <Field label="Straße und Hausnummer" span={6} error={errors.street}>
                <TextInput
                  value={form.street}
                  onChange={(value) => update('street', value)}
                  disabled={readOnly}
                />
              </Field>
              <Field label="PLZ" span={2}>
                <TextInput
                  value={form.postalCode}
                  onChange={(value) => update('postalCode', value)}
                  disabled={readOnly}
                />
              </Field>
              <Field label="Ort" span={4}>
                <TextInput
                  value={form.city}
                  onChange={(value) => update('city', value)}
                  disabled={readOnly}
                />
              </Field>
              <Field label="Telefon" span={4}>
                <TextInput
                  value={form.phone ?? ''}
                  onChange={(value) => update('phone', value)}
                  disabled={readOnly}
                />
              </Field>
              <Field label="E-Mail" span={4}>
                <TextInput
                  value={form.email ?? ''}
                  onChange={(value) => update('email', value)}
                  disabled={readOnly}
                />
              </Field>
              <Field label="Internet" span={4}>
                <TextInput
                  value={form.website ?? ''}
                  onChange={(value) => update('website', value)}
                  disabled={readOnly}
                />
              </Field>
            </div>
          </fieldset>

          <fieldset>
            <legend>Pflichtangaben auf Geschäftsbriefen (§ 35a GmbHG)</legend>
            <div className="form-grid">
              <Field label="Registergericht" span={4} hint="z. B. Amtsgericht Münster">
                <TextInput
                  value={form.registerCourt ?? ''}
                  onChange={(value) => update('registerCourt', value)}
                  disabled={readOnly}
                />
              </Field>
              <Field label="Handelsregisternummer" span={3} hint="z. B. HRB 12345">
                <TextInput
                  value={form.registerNumber ?? ''}
                  onChange={(value) => update('registerNumber', value)}
                  disabled={readOnly}
                />
              </Field>
              <Field label="Geschäftsführer" span={5} hint="Mehrere durch Komma trennen">
                <TextInput
                  value={form.managingDirectors ?? ''}
                  onChange={(value) => update('managingDirectors', value)}
                  disabled={readOnly}
                />
              </Field>
            </div>
          </fieldset>

          <fieldset>
            <legend>Steuer</legend>
            <div className="form-grid">
              <Field label="Steuernummer" span={4} error={errors.taxNumber}>
                <TextInput
                  value={form.taxNumber ?? ''}
                  onChange={(value) => update('taxNumber', value)}
                  invalid={Boolean(errors.taxNumber)}
                  disabled={readOnly}
                />
              </Field>
              <Field
                label="USt-IdNr."
                span={4}
                error={errors.vatId}
                hint="Pflicht bei EU-Geschäften"
              >
                <TextInput
                  value={form.vatId ?? ''}
                  onChange={(value) => update('vatId', value.toUpperCase())}
                  invalid={Boolean(errors.vatId)}
                  disabled={readOnly}
                />
              </Field>
              <Field label="Finanzamt" span={4}>
                <TextInput
                  value={form.taxOffice ?? ''}
                  onChange={(value) => update('taxOffice', value)}
                  disabled={readOnly}
                />
              </Field>
            </div>
          </fieldset>

          <fieldset>
            <legend>Bankverbindung</legend>
            <div className="form-grid">
              <Field label="Bank" span={4}>
                <TextInput
                  value={form.bankName ?? ''}
                  onChange={(value) => update('bankName', value)}
                  disabled={readOnly}
                />
              </Field>
              <Field label="IBAN" span={5} error={errors.iban}>
                <TextInput
                  value={form.iban ?? ''}
                  onChange={(value) => update('iban', value)}
                  invalid={Boolean(errors.iban)}
                  disabled={readOnly}
                />
              </Field>
              <Field label="BIC" span={3} error={errors.bic}>
                <TextInput
                  value={form.bic ?? ''}
                  onChange={(value) => update('bic', value.toUpperCase())}
                  invalid={Boolean(errors.bic)}
                  disabled={readOnly}
                />
              </Field>
              {form.iban ? (
                <div className="field">
                  <span className="field-hint">Auf dem Beleg: {formatIban(form.iban)}</span>
                </div>
              ) : null}
              <Field
                label="Antwortadresse für Rechnungen"
                span={6}
                hint="Kundenantworten auf Rechnungen und Gutschriften gehen hierher (Buchhaltung); leer = an den Versender"
                error={errors.accountingReplyEmail}
              >
                <TextInput
                  type="email"
                  value={form.accountingReplyEmail ?? ''}
                  onChange={(value) => update('accountingReplyEmail', value)}
                  placeholder="z. B. rechnung@fluvius.de"
                  invalid={Boolean(errors.accountingReplyEmail)}
                  disabled={readOnly}
                />
              </Field>
            </div>
          </fieldset>

          <fieldset>
            <legend>Rechnungswesen</legend>
            <div className="form-grid">
              <Field
                label="Kontenrahmen"
                span={3}
                hint="Nur vor der ersten Buchung änderbar"
              >
                <Select
                  value={form.chartOfAccounts}
                  onChange={(value) => update('chartOfAccounts', value)}
                  options={[
                    { value: 'SKR03', label: 'SKR03' },
                    { value: 'SKR04', label: 'SKR04' },
                  ]}
                  disabled={readOnly}
                />
              </Field>
              <Field label="DATEV-Beraternummer" span={3}>
                <TextInput
                  value={form.datevConsultantNumber ?? ''}
                  onChange={(value) => update('datevConsultantNumber', value)}
                  disabled={readOnly}
                />
              </Field>
              <Field label="DATEV-Mandantennummer" span={3}>
                <TextInput
                  value={form.datevClientNumber ?? ''}
                  onChange={(value) => update('datevClientNumber', value)}
                  disabled={readOnly}
                />
              </Field>
              <Field label="Intrastat: Bundesland" span={4} hint="Vorgabe für die Meldung">
                <Select
                  value={form.intrastatState ?? '05'}
                  onChange={(value) => update('intrastatState', value)}
                  options={BUNDESLAENDER.map((entry) => ({ value: entry.code, label: `${entry.code} ${entry.label}` }))}
                  disabled={readOnly}
                />
              </Field>
              <Field label="Intrastat: Verkehrszweig" span={4}>
                <Select
                  value={form.intrastatTransport ?? '3'}
                  onChange={(value) => update('intrastatTransport', value)}
                  options={INTRASTAT_TRANSPORT_MODES.map((entry) => ({ value: entry.code, label: `${entry.code} ${entry.label}` }))}
                  disabled={readOnly}
                />
              </Field>
              <Field label="Intrastat: Art des Geschäfts" span={4}>
                <Select
                  value={form.intrastatNature ?? '11'}
                  onChange={(value) => update('intrastatNature', value)}
                  options={INTRASTAT_TRANSACTION_TYPES.map((entry) => ({ value: entry.code, label: `${entry.code} ${entry.label}` }))}
                  disabled={readOnly}
                />
              </Field>
              <Field label="Zahlungsziel in Tagen" span={3}>
                <input autoComplete="off"
                  className="input numeric"
                  type="number"
                  min={0}
                  max={365}
                  value={form.defaultPaymentDays}
                  disabled={readOnly}
                  onChange={(event) => update('defaultPaymentDays', Number(event.target.value) || 0)}
                />
              </Field>
              <Field label="Schlusstext auf Rechnungen" span={12}>
                <TextArea
                  value={form.invoiceFooterText ?? ''}
                  onChange={(value) => update('invoiceFooterText', value)}
                  rows={2}
                  disabled={readOnly}
                />
              </Field>
            </div>
          </fieldset>
        </div>

        {!readOnly ? (
          <div className="card-footer">
            <button type="submit" className="btn btn-primary" disabled={save.isPending}>
              {save.isPending ? 'Wird gespeichert …' : 'Speichern'}
            </button>
          </div>
        ) : null}
      </div>
    </form>
  );
}

function NumberRangeSettings() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { can } = useAuth();
  const readOnly = !can('settings:write');

  const { data, isLoading } = useQuery({
    queryKey: ['number-ranges'],
    queryFn: () => api.get<{ ranges: NumberRange[] }>('/api/settings/number-ranges'),
  });

  const save = useMutation({
    mutationFn: (range: { key: string; pattern: string; reset: 'never' | 'yearly' }) =>
      api.patch(`/api/settings/number-ranges/${range.key}`, {
        pattern: range.pattern,
        reset: range.reset,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['number-ranges'] });
      toast.success('Der Nummernkreis wurde geändert.');
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Speichern fehlgeschlagen.'),
  });

  if (isLoading || !data) return <LoadingBlock />;

  return (
    <>
      <Alert kind="info">
        Belegnummern werden erst beim Festschreiben vergeben und sind danach unveränderlich. Bereits
        vergebene Nummern bleiben erhalten, auch wenn Sie das Muster ändern. Platzhalter:{' '}
        <span className="mono">{'{YYYY}'}</span> Jahr, <span className="mono">{'{MM}'}</span> Monat,{' '}
        <span className="mono">{'{NNNN}'}</span> laufende Nummer.
      </Alert>

      <div className="card">
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Belegart</th>
                <th>Muster</th>
                <th className="shrink">Zurücksetzen</th>
                <th className="shrink">Stand</th>
                <th className="shrink"></th>
              </tr>
            </thead>
            <tbody>
              {data.ranges.map((range) => (
                <NumberRangeRow
                  key={range.key}
                  range={range}
                  readOnly={readOnly}
                  onSave={(pattern, reset) => save.mutate({ key: range.key, pattern, reset })}
                  busy={save.isPending}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function NumberRangeRow({
  range,
  readOnly,
  onSave,
  busy,
}: {
  range: NumberRange;
  readOnly: boolean;
  onSave: (pattern: string, reset: 'never' | 'yearly') => void;
  busy: boolean;
}) {
  const [pattern, setPattern] = useState(range.pattern);
  const [reset, setReset] = useState(range.reset);

  const changed = pattern !== range.pattern || reset !== range.reset;
  const currentYear = String(new Date().getFullYear());
  const counter =
    range.counters.find((entry) => entry.period === currentYear) ??
    range.counters.find((entry) => entry.period === 'all');

  return (
    <tr>
      <td>{range.label}</td>
      <td>
        <input autoComplete="off"
          className="input mono"
          value={pattern}
          disabled={readOnly}
          onChange={(event) => setPattern(event.target.value)}
          style={{ maxWidth: 220 }}
        />
      </td>
      <td>
        <select
          className="select input"
          value={reset}
          disabled={readOnly}
          onChange={(event) => setReset(event.target.value as 'never' | 'yearly')}
          style={{ width: 120 }}
        >
          <option value="yearly">jährlich</option>
          <option value="never">nie</option>
        </select>
      </td>
      <td className="numeric muted">{counter?.lastNumber ?? 0}</td>
      <td>
        {!readOnly && changed ? (
          <button type="button" className="btn btn-sm btn-primary" disabled={busy} onClick={() => onSave(pattern, reset)}>
            Übernehmen
          </button>
        ) : null}
      </td>
    </tr>
  );
}
